import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'

// @MUI
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'

const StaffChip = ({ staff, acknowledged, onClick }) => (
  <Chip
    size="small"
    clickable
    onClick={onClick}
    avatar={
      <Avatar src={staff.userAccount?.photograph}>
        {staff.userAccount?.firstName?.[0]}
        {staff.userAccount?.lastName?.[0]}
      </Avatar>
    }
    label={`${staff.userAccount?.firstName ?? ''} ${
      staff.userAccount?.lastName ?? ''
    }`}
    variant="outlined"
    sx={{
      borderRadius: 999,
      fontWeight: 600,
      borderColor: acknowledged ? 'success.light' : 'divider',
      color: acknowledged ? 'success.dark' : 'text.secondary',
    }}
  />
)

const AcknowledgedBy = ({
  acknowledgedBy = [],
  announcementRequiresAcknowledgement,
}) => {
  const [staffList, setStaffList] = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    // Banned-from-events staff still receive announcements, so include them
    API.get('/employee-search?includeBannedFromEvents=true')
      .then((res) => {
        if (res.status === 200) {
          setStaffList(res.data)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  const { acknowledged, pending } = useMemo(() => {
    const byName = (a, b) => {
      const aName = `${a.userAccount?.firstName ?? ''} ${
        a.userAccount?.lastName ?? ''
      }`.toLowerCase()
      const bName = `${b.userAccount?.firstName ?? ''} ${
        b.userAccount?.lastName ?? ''
      }`.toLowerCase()
      return aName.localeCompare(bName)
    }
    return {
      acknowledged: staffList
        .filter((s) => acknowledgedBy.includes(s._id))
        .sort(byName),
      pending: staffList
        .filter((s) => !acknowledgedBy.includes(s._id))
        .sort(byName),
    }
  }, [staffList, acknowledgedBy])

  const verb = announcementRequiresAcknowledgement ? 'Acknowledged' : 'Read'
  const total = staffList.length
  const done = acknowledged.length
  const percent = total > 0 ? Math.round((done / total) * 100) : 0

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 2.5,
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Stack gap={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          useFlexGap
        >
          <Typography fontWeight={700}>{verb} By</Typography>
          {!loading && (
            <Typography variant="caption" color="text.secondary">
              {done} of {total} staff ({percent}%)
            </Typography>
          )}
        </Stack>

        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <LinearProgress
              variant="determinate"
              value={percent}
              color={percent === 100 ? 'success' : 'primary'}
              sx={{ borderRadius: 999, height: 6 }}
            />

            {acknowledged.length > 0 && (
              <Box>
                <Stack
                  direction="row"
                  gap={0.5}
                  alignItems="center"
                  marginBottom={0.75}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: '1rem', color: 'success.main' }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'text.secondary',
                    }}
                  >
                    {verb} ({acknowledged.length})
                  </Typography>
                </Stack>
                <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
                  {acknowledged.map((staff) => (
                    <StaffChip
                      key={staff._id}
                      staff={staff}
                      acknowledged
                      onClick={() =>
                        staff.userAccount?._id &&
                        navigate(`/employees/${staff.userAccount._id}`)
                      }
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {pending.length > 0 && (
              <Box>
                <Stack
                  direction="row"
                  gap={0.5}
                  alignItems="center"
                  marginBottom={0.75}
                >
                  <HourglassEmptyIcon
                    sx={{ fontSize: '1rem', color: 'warning.main' }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'text.secondary',
                    }}
                  >
                    Awaiting ({pending.length})
                  </Typography>
                </Stack>
                <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
                  {pending.map((staff) => (
                    <StaffChip
                      key={staff._id}
                      staff={staff}
                      acknowledged={false}
                      onClick={() =>
                        staff.userAccount?._id &&
                        navigate(`/employees/${staff.userAccount._id}`)
                      }
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}
      </Stack>
    </Paper>
  )
}

export default AcknowledgedBy
